<template>
  <card-container
    :id="pageId"
    class="welcome-texts"
    :is-loading="status.isLoading"
    :loading-message="status.spinnerMessage"
    button-location="top"
    @submit="loadData"
  >
    <template #title>
      Welcome Texts
    </template>

    <template #top-form>
      <fieldset>
        <div class="row align-items-center">
          <label class="col-3 control-label"> Caller Name</label>
          <div class="col-6">
            <sanitized-input
              v-model="searchConditions.customerName"
              placeholder="Please input the caller name"
            />
          </div>
        </div>
        <div class="row align-items-center">
          <label class="col-3 control-label">
            Caller phone number
          </label>
          <div class="col-6">
            <phone-number-input
              id="customer-phone"
              v-model="searchConditions.customerPhoneNumber"
              :page-id="pageId"
            />
          </div>
        </div>
        <div class="row align-items-center">
          <label class="col-3 control-label">Message text</label>
          <div class="col-6">
            <sanitized-input
              v-model="searchConditions.messageText"
              placeholder="Please input message text"
            />
          </div>
        </div>
        <div class="form-submit-button-group">
          <p-button
            type="purple"
            round
            native-type="submit"
            :disabled="status.isLoading"
          >
            Search
          </p-button>

          <p-button
            type="yellow"
            round
            :disabled="status.isLoading"
            @click="resetForm"
          >
            Reset
          </p-button>
        </div>
      </fieldset>

      <div class="welcome-texts-toolbar mt-10">
        <div class="toolbar-left">
          <el-radio-group
            v-model="statusGroup"
            class="toolbar-tabs"
            @change="onStatusGroupChange"
          >
            <el-radio-button :label="WelcomeTextInboxCallerStatusGroup.Inbox">
              Inbox
            </el-radio-button>
            <el-radio-button :label="WelcomeTextInboxCallerStatusGroup.Sent">
              Sent
            </el-radio-button>
          </el-radio-group>

          <button
            type="button"
            class="refresh-btn"
            aria-label="Refresh"
            :disabled="status.isLoading"
            @click="refreshData"
          >
            <i class="fa fa-refresh" />
          </button>
        </div>

        <div class="toolbar-pagination">
          <pagination
            :is-loading="status.isLoading"
            :current-page="status.currentPage"
            :total-pages="status.totalPages"
            @load-page-data="loadData"
          />
        </div>
      </div>
    </template>

    <template #content>
      <table-view-container
        ref="tableViewRef"
        :page-id="pageId"
        :data="welcomeTextInboxCallers"
        :table-columns="tableColumns"
        :custom-cell-style-func="customCellStyle"
        :height="800"
        @columnClick="handleColumnClick"
        @rowClick="handleRowClick"
      >
        <template
          v-if="welcomeTextInboxCallers.length > 0"
          #custom-first-column
        >
          <el-table-column type="expand">
            <template #default="scope">
              <TextInboxDetails
                :textInboxCaller="scope.row"
                :defaultMessage="defaultMessage"
                :activeRowIndex="activeRowIndex"
                @inbox-caller-handled="removeCallerFromInboxList"
              />
            </template>
          </el-table-column>
        </template>

        <template #right-fixed-columns>
          <el-table-column
            prop="action-col"
            fixed="right"
            label=""
            width="190"
          >
            <template #default="scope">
              <div
                v-if="availableActions.includes(scope.row.action)"
                class="action-btn"
                :class="scope.row.action"
                @click.stop="handleAction(scope.row)"
              >
                {{ getActionTxt(scope.row.action) }}
              </div>
            </template>
          </el-table-column>
        </template>
      </table-view-container>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { defineAsyncComponent, onMounted, reactive, ref, Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { startCase } from 'lodash';
import delay from 'yoctodelay';
import events from '@/events';

import util from '@/Utils/util';
import { substitutePlaceholders } from '@/Utils/stringUtil';
import { useProfile } from '@/use/profile';
import { useSwalDialog } from '@/use/swalDialog';
import type { TableColumnType } from '@/models/table';
import {
  WelcomeTextInboxCallerAction,
  WelcomeTextInboxCallerStatusGroup,
  type WelcomeTextInboxCaller,
} from '@/models/welcomeTextInboxCaller';
import { callerService } from '@/services/caller';
import { welcomeTextInboxCallerService } from '@/services/welcomeTextInboxCaller';
import { welcomeSmsText, positiveSmsText, neutralSmsText } from '@/constants/welcomeText';
import TableView from '@/components/UIComponents/TableView.vue';

const TextInboxDetails = defineAsyncComponent(() => import('./WelcomeText/TextInboxDetails.vue') as any);

const pageId = 'welcome-texts';
const paginationLimit = 30;

const tableColumns: TableColumnType[] = [
  { label: 'Name', field: 'fullName', width: 140, isDisableSort: true },
  { label: 'PhoneNumber', field: 'phoneNumber', width: 120, isDisableSort: true },
  { label: 'First Ride', field: 'firstRideDate', width: 100, isDisableSort: true, formatter: (value: string) => util.formatDateTime(value, 'MMM D, YYYY') },
  { label: 'Ride Type', field: 'firstRideLocationType', width: 100, isDisableSort: true },
  { label: 'Rides', field: 'rideCount', width: 60, isDisableSort: true },
  { label: 'Welcome operator name', field: 'firstWelcomeMessageOperatorFirstName', width: 120, isDisableSort: true },
  { label: 'Status', field: 'status', width: 130, isDisableSort: true, formatter: (value: string) => getStatusHtml(value) },
  { label: 'Latest Reply', field: 'lastSmsReply', isDisableSort: true, customClass: 'last-reply-message' },
];

const defaultMessageMapping = {
  [WelcomeTextInboxCallerAction.SendPositive]: positiveSmsText,
  [WelcomeTextInboxCallerAction.SendNeutral]: neutralSmsText,
};

const availableActions = [
  WelcomeTextInboxCallerAction.NewCaller,
  WelcomeTextInboxCallerAction.SendNegative,
  WelcomeTextInboxCallerAction.SendNeutral,
  WelcomeTextInboxCallerAction.SendPositive,
];

const route = useRoute();
const router = useRouter();
const { currentUser } = useProfile();
const { showErrorPopupDialog } = useSwalDialog();

const searchConditions = reactive({
  customerName: '',
  customerPhoneNumber: '',
  messageText: '',
});
const tableViewRef = ref<InstanceType<typeof TableView> | null>(null);
const welcomeTextInboxCallers: Ref<WelcomeTextInboxCaller[]> = ref([]);
const status = reactive({
  isLoading: false,
  spinnerMessage: '',
  totalPages: 1,
  currentPage: 1,
});
const activeRowIndex = ref(-1);
const defaultMessage = ref('');
const statusGroup = ref(WelcomeTextInboxCallerStatusGroup.Inbox);

const getStatusHtml = (status: string) => `<span class="status ${ status }">${ startCase(status) }</span>`;

const getActionTxt = (action: string) => {
  switch (action) {
    case WelcomeTextInboxCallerAction.NewCaller:
      return 'Send Welcome';
    case WelcomeTextInboxCallerAction.SendPositive:
      return 'Send Positive Follow-up';
    case WelcomeTextInboxCallerAction.SendNegative:
      return 'Send Negative Follow-up';
    case WelcomeTextInboxCallerAction.SendNeutral:
      return 'Send Neutral Follow-Up';
    default:
      return '';
  }
};

const customCellStyle = (param: any) => {
  const { column } = param;

  if (column.property !== 'phoneNumber') {
    return;
  }

  return {
    cursor: 'pointer',
    color: '#00F',
    'text-decoration': 'underline',
  };
};

const handleRowClick = (row: WelcomeTextInboxCaller, column: any) => {
  if (column.property === 'action-col') {
    return;
  }

  tableViewRef.value?.toggleRowExpansion(row);
};

const resetForm = () => {
  searchConditions.customerName = '';
  searchConditions.customerPhoneNumber = '';
  searchConditions.messageText = '';
  loadData(1);
};

const handleColumnClick = async (row: WelcomeTextInboxCaller, column: any) => {
  if (column?.property !== 'phoneNumber') {
    return;
  }

  if (!row.phoneNumber) {
    return;
  }

  router.push({
    name: 'WelcomeTexts',
    query: {
      ...route.query,
      phoneNumber: row.phoneNumber,
    },
  });

  await delay(100);

  events.emit('refresh-right-panel');
};

const handleAction = (row: WelcomeTextInboxCaller) => {
  activeRowIndex.value = row.index ?? 1;
  defaultMessage.value = '';
  switch (row.action) {
    case WelcomeTextInboxCallerAction.NewCaller:
      return sendWelcomeSms(row);
    case WelcomeTextInboxCallerAction.SendPositive:
    case WelcomeTextInboxCallerAction.SendNeutral:
      defaultMessage.value = defaultMessageMapping[row.action];
      break;
    case WelcomeTextInboxCallerAction.SendNegative:
    default:
      defaultMessage.value = '';
      break;
  }

  tableViewRef.value?.toggleRowExpansion(row, true);
};

const removeCallerFromInboxList = (callerId: string) => {
  if (statusGroup.value !== WelcomeTextInboxCallerStatusGroup.Inbox) {
    return;
  }

  welcomeTextInboxCallers.value = welcomeTextInboxCallers.value.filter(
    item => item.callerId !== callerId,
  );
};

const sendWelcomeSms = async (row: WelcomeTextInboxCaller) => {
  const message = substitutePlaceholders(welcomeSmsText, {
    customerFirstName: row.firstName ?? '',
    operatorFirstName: currentUser.value?.firstName ?? '',
  });

  status.spinnerMessage = 'Sending Welcome SMS...';
  status.isLoading = true;

  try {
    await callerService.sendWelcomeTextSmsToCaller({
      callerId: row.callerId,
      message,
    });

    removeCallerFromInboxList(row.callerId);
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  } finally {
    status.isLoading = false;
  }
};

const loadData = async (currentPage: number = 1) => {
  const offset = (currentPage - 1) * paginationLimit;
  status.currentPage = currentPage;
  status.spinnerMessage = 'Loading Data...';
  status.isLoading = true;

  try {
    const response = await welcomeTextInboxCallerService.fetchWelcomeTextInboxCallers({
      pagination: {
        offset,
        limit: paginationLimit,
      },
      statusGroup: statusGroup.value,
      callerName: searchConditions.customerName,
      phoneNumber: searchConditions.customerPhoneNumber,
      messageText: searchConditions.messageText,
    });

    status.totalPages = Math.ceil(response.count / paginationLimit) || 1;
    welcomeTextInboxCallers.value = response.rows.map((item, index) => ({
      ...item,
      index: offset + index + 1,
      fullName: `${item.firstName} ${item.lastName}`,
      lastSmsReply: item.lastSmsReply || '--',
    }));
  } finally {
    status.isLoading = false;
  }
};

const onStatusGroupChange = () => {
  loadData(1);
};

const refreshData = () => {
  loadData(status.currentPage);
};

onMounted(loadData);
</script>

<style lang="scss">
.welcome-texts {
  .phone-number-input {
    width: 100%;

    .phone-number-input-wrapper {
      width: 100%;

      .country-code {
        flex-shrink: 0;
      }

      .phone {
        min-width: 0;
      }
    }
  }

  .welcome-texts-toolbar {
    display: flex;
    align-items: center;

    .toolbar-left {
      display: flex;
      align-items: stretch;
      width: 50%;
      gap: 12px;

      .toolbar-tabs {
        flex: 1;
        display: flex;
        align-items: stretch;

        .el-radio-button {
          flex: 1;
          display: flex;
          align-items: stretch;

          .el-radio-button__inner {
            width: 100%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
        }
      }
    }

    .toolbar-pagination {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 50%;

      .pagination-group {
        width: auto;
      }
    }

    .refresh-btn {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      align-self: stretch;
      border: none;
      background: transparent;
      font-size: 20px;
      cursor: pointer;
      padding: 0 12px;
      min-width: 40px;
      color: #606266;
      margin-bottom: 5px !important;

      &:hover:not(:disabled) {
        color: #eb5e38;
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }
    }
  }

  .action-btn {
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    width: fit-content;
    padding: 6px 12px;
    margin-left: auto;
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    cursor: pointer;

    &.send_negative {
      background-color: #fff;
      color: #0a0a0a;
    }

    &.send_positive {
      background-color: #009966;
    }

    &.send_neutral {
      background-color: #45556c;
    }

    &.new_caller {
      background-color: #0084d1;
    }
  }

  .status {
    padding: 2px 8px;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    border: 1px solid;
    border-radius: 8px;

    &.replied {
      background-color: #d0fae5;
      border-color: #a4f4cf;
      color: #006045;
    }

    &.unread {
      background-color: #ffe2e2;
      border-color: #ffc9c9;
      color: #9f0712;
    }

    &.handled {
      background-color: #f1f5f9;
      border-color: #e2e8f0;
      color: #1d293d;
    }

    &.new {
      background-color: #dff2fe;
      border-color: #b8e6fe;
      color: #00598a;
    }

    &.welcome_sent {
      background-color: #fef3c6;
      border-color: #fee685;
      color: #973c00;
    }
  }

  .last-reply-message {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
  }
}
</style>

